import { useEffect, useState } from 'react'
import type { CSSProperties } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { useAuth } from '../auth/AuthProvider'
import { Spinner } from '../ui/Spinner'

/**
 * AuthCallbackScreen — parses `?token=<jwt>`, persists to localStorage,
 * strips the token from the URL via `history.replaceState` (D-84 privacy —
 * keep JWT out of browser history / referer headers), then navigates home.
 *
 * Copy per UI-SPEC §Copywriting Contract:
 *   "Signing you in…"
 *   "That link didn't work. Try sending a new one." + "Back to sign in"
 *
 * Token shape check is defensive, not cryptographic: we just confirm the
 * value looks like `header.payload.signature`. Real verification happens
 * server-side on the next authenticated request; a bad JWT surfaces as
 * URN_AUTH_INVALID_TOKEN which apiGet handles (clears JWT, lands on /).
 */

function looksLikeJwt(token: string): boolean {
  const parts = token.split('.')
  return parts.length === 3 && parts.every((p) => p.length > 0)
}

export function AuthCallback() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { setJwt } = useAuth()
  const [error, setError] = useState(false)

  useEffect(() => {
    const token = searchParams.get('token')
    if (!token || !looksLikeJwt(token)) {
      setError(true)
      return
    }

    setJwt(token)
    // Strip ?token= from the URL so it never reaches history / referer /
    // analytics. Replace to '/' — the App shell picks MapView from here.
    window.history.replaceState({}, '', '/')
    navigate('/', { replace: true })
  }, [searchParams, setJwt, navigate])

  if (error) {
    return (
      <main style={shellStyle}>
        <section style={cardStyle} role="alert">
          <h1 style={headingStyle}>That link didn&rsquo;t work.</h1>
          <p style={bodyStyle}>Try sending a new one.</p>
          <button
            type="button"
            onClick={() => navigate('/signin', { replace: true })}
            style={buttonStyle}
          >
            Back to sign in
          </button>
        </section>
      </main>
    )
  }

  return (
    <main style={shellStyle}>
      <section
        style={cardStyle}
        role="status"
        aria-live="polite"
      >
        <div style={spinnerRowStyle}>
          <Spinner size={24} />
          <p style={bodyStyle}>Signing you in&hellip;</p>
        </div>
      </section>
    </main>
  )
}

// --- Styles (inline, token-driven) — same card as SignInScreen.

const shellStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '100dvh',
  padding: 'var(--space-4)',
  background: 'var(--color-secondary)',
}

const cardStyle: CSSProperties = {
  width: '100%',
  maxWidth: 400,
  padding: 'var(--space-6)',
  background: 'var(--color-dominant)',
  borderRadius: 16,
  boxShadow: 'var(--color-shadow)',
  textAlign: 'center',
}

const headingStyle: CSSProperties = {
  margin: 0,
  marginBottom: 'var(--space-3)',
  fontSize: 'var(--font-size-heading)',
  fontWeight: 600,
  color: 'var(--color-text-primary)',
}

const bodyStyle: CSSProperties = {
  margin: 0,
  fontSize: 'var(--font-size-body)',
  color: 'var(--color-text-secondary)',
}

const spinnerRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 12,
}

const buttonStyle: CSSProperties = {
  display: 'block',
  width: '100%',
  height: 44,
  marginTop: 'var(--space-4)',
  borderRadius: 12,
  background: 'var(--color-accent)',
  color: '#FFFFFF',
  fontSize: 'var(--font-size-body)',
  fontWeight: 600,
  border: 0,
  cursor: 'pointer',
}
